/**
 * Where a plan's labels go so they do not sit on one another — the wall
 * letters, the item tags (T1, C2…) and the device tags (R1, SW2…) of
 * `planSvg`. Pure, in the plan's pixels.
 *
 * Greedy, in the order the labels are handed: each takes the first of its
 * candidate spots that is clear of everything placed before it and of the
 * obstacles, and inside the frame; when none is clear, the one that overlaps
 * least. So the caller's order is a priority — the earlier a label, the more
 * likely it keeps its own spot.
 */

// the tags are bold Helvetica: a character is ~0.62 em wide, and the white
// halo they are stroked with adds a little all round
const CHAR_EM = 0.62;
const HALO_PX = 1.5;

/** A centred text label's box. → { x0, y0, x1, y1 } */
function textBox(x, y, text, sizePx) {
  const hw = (String(text).length * CHAR_EM * sizePx) / 2 + HALO_PX;
  const hh = sizePx / 2 + HALO_PX;
  return { x0: x - hw, y0: y - hh, x1: x + hw, y1: y + hh };
}

/** A circle's box (a wall letter's tag, a finding's dot, a device). */
function circleBox(x, y, r) {
  return { x0: x - r, y0: y - r, x1: x + r, y1: y + r };
}

const overlapArea = (a, b) =>
  Math.max(0, Math.min(a.x1, b.x1) - Math.max(a.x0, b.x0)) *
  Math.max(0, Math.min(a.y1, b.y1) - Math.max(a.y0, b.y0));

const outside = (b, frame) =>
  frame && (b.x0 < 0 || b.y0 < 0 || b.x1 > frame.w || b.y1 > frame.h);

/**
 * Place labels. `labels`: [{ candidates: [{ x, y }], box(x, y) → box,
 * ignore?: [box] }] — candidates in order of preference, `ignore` obstacles
 * this label may sit on (its own device). `obstacles`: [box] fixed marks.
 * `frame`: { w, h } or null. → [{ x, y, moved }] in the labels' order, `moved`
 * when it left its first candidate.
 */
function placeLabels(labels, obstacles = [], frame = null) {
  const placed = [];
  return labels.map((label) => {
    const ignore = new Set(label.ignore || []);
    const blockers = [...obstacles.filter((o) => !ignore.has(o)), ...placed];
    let best = null;
    for (const [i, c] of label.candidates.entries()) {
      const b = label.box(c.x, c.y);
      if (outside(b, frame)) continue;
      const cost = blockers.reduce((sum, o) => sum + overlapArea(b, o), 0);
      if (!best || cost < best.cost) best = { c, b, cost, i };
      if (cost === 0) break;
    }
    if (!best) {
      // every spot leaves the frame: keep the first
      const c = label.candidates[0];
      best = { c, b: label.box(c.x, c.y), i: 0 };
    }
    placed.push(best.b);
    return { x: best.c.x, y: best.c.y, moved: best.i > 0 };
  });
}

/**
 * The spots round a device a tag may take, nearest first: its own offset
 * `{ dx, dy }` (both non-zero), that offset's three mirror images, then
 * straight above, below and to each side — and the same again further out.
 */
function ringCandidates(x, y, { dx, dy }) {
  const out = [];
  for (const k of [1, 1.7, 2.4]) {
    const ax = dx * k;
    const ay = dy * k;
    out.push(
      { x: x + ax, y: y + ay },
      { x: x + ax, y: y - ay },
      { x: x - ax, y: y + ay },
      { x: x - ax, y: y - ay },
      { x, y: y - Math.abs(ay) * 1.5 },
      { x, y: y + Math.abs(ay) * 1.5 },
      { x: x + Math.abs(ax) * 1.5, y },
      { x: x - Math.abs(ax) * 1.5, y }
    );
  }
  return out;
}

/**
 * The spots an item's tag may take: the item's own point, then a line above
 * and below it, then beside it, then two lines off — all near enough that the
 * tag still reads as the item's. `sizePx`: the tag's font size.
 */
function itemCandidates(x, y, sizePx) {
  const v = sizePx * 1.3;
  const h = sizePx * 2.2;
  return [
    { x, y },
    { x, y: y - v },
    { x, y: y + v },
    { x: x + h, y },
    { x: x - h, y },
    { x, y: y - 2 * v },
    { x, y: y + 2 * v },
    { x: x + h, y: y - v },
    { x: x - h, y: y - v },
    { x: x + h, y: y + v },
    { x: x - h, y: y + v },
  ];
}

/**
 * The spots along a wall a letter's tag may take: its own spot, then slid
 * along the wall a tag's width at a time, each way in turn, as far as the
 * wall's half-length allows. `along`: the wall's unit direction, in px.
 */
function slideCandidates(x, y, along, halfLength, step) {
  const out = [{ x, y }];
  for (let k = 1; k * step <= halfLength; k += 1) {
    for (const sign of [1, -1]) {
      out.push({
        x: x + sign * k * step * along.x,
        y: y + sign * k * step * along.z,
      });
    }
  }
  return out;
}

module.exports = {
  textBox,
  circleBox,
  placeLabels,
  ringCandidates,
  itemCandidates,
  slideCandidates,
};
